/**
 * Centralized entity field configurations.
 *
 * Single source of truth for field metadata (key, label, type, options,
 * editability) for Coverage and MedicalProvider entities.
 *
 * Used by:
 * - Entity detail views (via `toFieldDefinitions()`)
 * - Suggestion cards (via `getFieldLabels()`)
 */

import type {
  CoverageCategory,
  CoverageLiabilityStatus,
  CoverageStatus,
  PolicyType,
  ProviderTreatmentStatus,
  TreatmentCareLevel,
  TreatmentRecordRequestStatus,
} from '@/services/api/generated/entitySyncService';

import type { BaseFieldConfig, FieldDefinition } from './types';

// ============================================================================
// Coverage
// ============================================================================

export const COVERAGE_CATEGORY_OPTIONS: {
  value: CoverageCategory;
  label: string;
}[] = [
  { value: 'FIRST_PARTY', label: 'First Party' },
  { value: 'FIRST_PARTY_HEALTH', label: 'First Party Health' },
  { value: 'THIRD_PARTY', label: 'Third Party' },
  { value: 'NOT_LIABLE_INVOLVED_PARTY', label: 'Not Liable Involved Party' },
  { value: 'UNCLEAR', label: 'Unclear' },
  { value: 'PROPERTY', label: 'Property' },
  { value: 'WORKERS_COMPENSATION', label: "Workers' Compensation" },
];

export const POLICY_TYPE_OPTIONS: { value: PolicyType; label: string }[] = [
  { value: 'AUTO', label: 'Auto' },
  { value: 'HOMEOWNERS', label: 'Homeowners' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'UMBRELLA', label: 'Umbrella' },
  { value: 'WORKERS_COMPENSATION', label: "Workers' Compensation" },
  { value: 'GENERAL_LIABILITY', label: 'General Liability' },
  { value: 'OTHER', label: 'Other' },
];

export const COVERAGE_STATUS_OPTIONS: {
  value: CoverageStatus;
  label: string;
}[] = [
  { value: 'NEEDS_APPROVAL', label: 'Needs Approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
];

export const LIABILITY_STATUS_OPTIONS: {
  value: CoverageLiabilityStatus;
  label: string;
}[] = [
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PARTIALLY_ACCEPTED', label: 'Partially Accepted' },
  { value: 'LIABILITY_UNKNOWN', label: 'Unknown' },
  { value: 'UNDER_INVESTIGATION', label: 'Under Investigation' },
];

export const COVERAGE_FIELD_CONFIGS: BaseFieldConfig[] = [
  {
    key: 'carrier_name',
    label: 'Carrier Name',
    type: 'text',
    isEditable: true,
  },
  {
    key: 'category',
    label: 'Category',
    type: 'select',
    options: COVERAGE_CATEGORY_OPTIONS,
    isEditable: true,
  },
  {
    key: 'policy_type',
    label: 'Policy Type',
    type: 'select',
    options: POLICY_TYPE_OPTIONS,
    isEditable: true,
  },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: COVERAGE_STATUS_OPTIONS,
    isEditable: true,
  },
  {
    key: 'liability_status',
    label: 'Liability Status',
    type: 'select',
    options: LIABILITY_STATUS_OPTIONS,
    isEditable: true,
  },
  {
    key: 'carrier_division_id',
    label: 'Carrier Directory Link',
    type: 'readonly',
    isEditable: false,
  },
];

// ============================================================================
// Medical Provider
// ============================================================================

export const TREATMENT_STATUS_OPTIONS: {
  value: ProviderTreatmentStatus;
  label: string;
}[] = [
  { value: 'TREATING', label: 'Treating' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'DEVIATING', label: 'Deviating' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

export const CARE_LEVEL_OPTIONS: {
  value: TreatmentCareLevel;
  label: string;
}[] = [
  { value: 'EMERGENCY_CARE', label: 'Emergency Care' },
  { value: 'CONSERVATIVE_CARE', label: 'Conservative Care' },
  { value: 'DIAGNOSTIC_TESTING', label: 'Diagnostic Testing' },
  { value: 'SPECIALIST_CARE', label: 'Specialist Care' },
  { value: 'SURGICAL_CARE', label: 'Surgical Care' },
];

export const PROVIDER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'PRACTITIONER', label: 'Practitioner' },
];

export const RECORD_REQUEST_STATUS_LABELS: Record<
  TreatmentRecordRequestStatus,
  string
> = {
  not_ready: 'Not Ready',
  pending: 'Pending',
  request_submitted: 'Submitted',
  request_received: 'Received',
  awaiting_invoice: 'Awaiting Invoice',
  pending_payment: 'Pending Payment',
  records_pending: 'Records Pending',
  records_delivered: 'Delivered',
  records_ingested: 'Ingested',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  auto_retried: 'Auto Retried',
};

export const MEDICAL_PROVIDER_FIELD_CONFIGS: BaseFieldConfig[] = [
  { key: 'name', label: 'Name', type: 'text', isEditable: true },
  {
    key: 'status',
    label: 'Treatment Status',
    type: 'select',
    options: TREATMENT_STATUS_OPTIONS,
    isEditable: true,
  },
  {
    key: 'care_level',
    label: 'Care Level',
    type: 'select',
    options: CARE_LEVEL_OPTIONS,
    isEditable: true,
  },
  {
    key: 'provider_type',
    label: 'Provider Type',
    type: 'select',
    options: PROVIDER_TYPE_OPTIONS,
    isEditable: true,
  },
  { key: 'specialty', label: 'Specialty', type: 'text', isEditable: false },
  {
    key: 'treatment_start_date',
    label: 'Treatment Start',
    type: 'date',
    isEditable: false,
  },
  {
    key: 'treatment_end_date',
    label: 'Treatment End',
    type: 'date',
    isEditable: false,
  },
  { key: 'notes', label: 'Notes', type: 'textarea', isEditable: true },
  {
    key: 'description',
    label: 'Description',
    type: 'textarea',
    isEditable: true,
  },
  {
    key: 'provider_service_id',
    label: 'Provider Directory Link',
    type: 'readonly',
    isEditable: false,
  },
];

// ============================================================================
// Derivation Helpers
// ============================================================================

/** Convert BaseFieldConfig[] → Record<key, label> for suggestion card label maps */
export function getFieldLabels(
  configs: BaseFieldConfig[],
): Record<string, string> {
  return Object.fromEntries(configs.map((f) => [f.key, f.label]));
}

/**
 * Convert BaseFieldConfig[] → FieldDefinition[] with a default getValue.
 *
 * The default getValue reads `entity[config.key]` and converts to string.
 * Use the overrides parameter to provide custom getValue or renderCustomValue
 * for specific fields.
 */
export function toFieldDefinitions(
  configs: BaseFieldConfig[],
  overrides?: Partial<Record<string, Partial<FieldDefinition>>>,
): FieldDefinition[] {
  return configs.map((config) => ({
    ...config,
    getValue: (e: unknown) => {
      const entity = e as Record<string, unknown> | null;
      const val = entity?.[config.key];
      return val == null ? null : String(val);
    },
    ...overrides?.[config.key],
  }));
}
